import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useRates } from '@/contexts/RatesContext';
import { DEFAULT_RATES, mergeRates } from '@/lib/generation';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  Users, Key, Plus, Trash2, Eye, EyeOff, ExternalLink, Check, X,
  Settings, ChevronRight, Shield, UserPlus, Crown, BarChart3, DollarSign, Loader2, Palette, Image as ImageIcon, Upload, Bell, AtSign, Mail, PlugZap, AlertTriangle
} from 'lucide-react';

const PROVIDER_GROUPS = [
  {
    category: 'AI Generation',
    providers: [
      { key: 'openai_api_key', label: 'OpenAI', desc: 'Image generation (GPT Image 1)', url: 'https://platform.openai.com/api-keys' },
      { key: 'gemini_api_key', label: 'Google Gemini', desc: 'Text & image generation', url: 'https://aistudio.google.com/apikey' },
    ]
  },
  {
    category: 'Video Generation',
    providers: [
      { key: 'kling_api_key', label: 'Kling', desc: 'Kling video generation', url: 'https://kling.ai/dev/api-key' },
      { key: 'google_ai_api_key', label: 'Google AI (Veo)', desc: 'Veo video generation', url: 'https://aistudio.google.com/apikey' },
      { key: 'seedance_api_key', label: 'Seedance (ByteDance)', desc: 'Seedance video gen', url: 'https://seedance.ai' },
    ]
  },
  {
    category: 'Audio',
    providers: [
      { key: 'elevenlabs_api_key', label: 'ElevenLabs', desc: 'Voice / TTS generation', url: 'https://elevenlabs.io' },
    ]
  }
];

export default function TeamSettings() {
  const { user } = useAuth();
  const [teams, setTeams] = useState([]);
  const [activeTeam, setActiveTeam] = useState(null);
  const [tab, setTab] = useState('keys');
  const [pendingTeamInvites, setPendingTeamInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [apiKeys, setApiKeys] = useState({});
  const [showKeys, setShowKeys] = useState({});
  const [savingKeys, setSavingKeys] = useState(false);
  const [keyTests, setKeyTests] = useState({});

  const testKey = async (keyName, value) => {
    if (!activeTeam) return;
    setKeyTests(prev => ({ ...prev, [keyName]: { loading: true } }));
    try {
      const res = await api.post(`/teams/${activeTeam.id}/api-keys/test`, { key_name: keyName, ...(value ? { value } : {}) });
      setKeyTests(prev => ({ ...prev, [keyName]: res.data }));
      return res.data;
    } catch (err) {
      const r = { ok: false, message: err.response?.data?.detail || 'Test failed' };
      setKeyTests(prev => ({ ...prev, [keyName]: r }));
      return r;
    }
  };
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [showNewTeam, setShowNewTeam] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const [usageData, setUsageData] = useState(null);
  const [loadingUsage, setLoadingUsage] = useState(false);
  const { refreshRates } = useRates();
  const [rateForm, setRateForm] = useState(DEFAULT_RATES);
  const [savingRates, setSavingRates] = useState(false);
  const [brandName, setBrandName] = useState('');
  const [brandLogo, setBrandLogo] = useState('');
  const [brandColor, setBrandColor] = useState('#FF4500');
  const [savingBrand, setSavingBrand] = useState(false);
  const logoInputRef = useRef(null);
  const [prefs, setPrefs] = useState({ mentions: false, invites: true, welcome: true });
  const [savingPref, setSavingPref] = useState(false);

  useEffect(() => {
    api.get('/auth/me').then(r => setPrefs({
      mentions: !!r.data.email_mention_notifications,
      invites: r.data.email_invite_notifications !== false,
      welcome: r.data.email_welcome_notifications !== false,
    })).catch(() => {});
  }, []);

  const PREF_FIELD = {
    mentions: 'email_mention_notifications',
    invites: 'email_invite_notifications',
    welcome: 'email_welcome_notifications',
  };

  const togglePref = async (key) => {
    const next = !prefs[key];
    setPrefs(p => ({ ...p, [key]: next }));
    setSavingPref(true);
    try {
      await api.put('/auth/preferences', { [PREF_FIELD[key]]: next });
      toast.success('Preference saved');
    } catch {
      setPrefs(p => ({ ...p, [key]: !next }));
      toast.error('Failed to update preference');
    } finally { setSavingPref(false); }
  };

  const loadTeams = useCallback(async () => {
    try {
      const res = await api.get('/teams');
      setTeams(res.data);
      const active = res.data.find(t => t.is_active);
      if (active) loadTeamDetail(active.id);
    } catch {
      toast.error('Failed to load teams');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTeams(); }, [loadTeams]);

  const loadTeamDetail = async (teamId) => {
    try {
      const res = await api.get(`/teams/${teamId}`);
      setActiveTeam(res.data);
      setApiKeys({});
      setUsageData(null);
    } catch {}
  };

  const loadUsage = async (teamId) => {
    if (!teamId) return;
    setLoadingUsage(true);
    try {
      const res = await api.get(`/teams/${teamId}/usage?days=30`);
      setUsageData(res.data);
    } catch {}
    finally { setLoadingUsage(false); }
  };

  const handleSwitchTeam = async (teamId) => {
    try {
      await api.put(`/auth/active-team?team_id=${teamId}`);
      await loadTeams();
      toast.success('Switched team');
    } catch {
      toast.error('Failed to switch');
    }
  };

  const handleCreateTeam = async () => {
    if (!newTeamName.trim()) return;
    try {
      const res = await api.post('/teams', { name: newTeamName.trim() });
      toast.success('Team created!');
      setNewTeamName('');
      setShowNewTeam(false);
      await loadTeams();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const handleSaveKeys = async () => {
    if (!activeTeam) return;
    const keysToSave = {};
    Object.entries(apiKeys).forEach(([k, v]) => {
      if (v && v.trim()) keysToSave[k] = v.trim();
    });
    if (Object.keys(keysToSave).length === 0) {
      toast.error('No keys to save');
      return;
    }
    setSavingKeys(true);
    try {
      await api.put(`/teams/${activeTeam.id}/api-keys`, keysToSave);
      setApiKeys({});
      await loadTeamDetail(activeTeam.id);
      const results = await Promise.all(Object.keys(keysToSave).map(k => testKey(k)));
      const bad = results.filter(r => r && !r.ok).length;
      if (bad) toast.warning(`Saved, but ${bad} key${bad > 1 ? 's' : ''} failed verification — see details below`);
      else toast.success('API keys saved & verified!');
    } catch {
      toast.error('Failed to save keys');
    } finally {
      setSavingKeys(false);
    }
  };

  const handleDeleteKey = async (keyName) => {
    if (!activeTeam) return;
    try {
      await api.delete(`/teams/${activeTeam.id}/api-keys/${keyName}`);
      toast.success('Key removed');
      await loadTeamDetail(activeTeam.id);
    } catch {
      toast.error('Failed to remove key');
    }
  };

  const handleInvite = async () => {
    if (!activeTeam || !inviteEmail.trim()) return;
    try {
      await api.post(`/teams/${activeTeam.id}/invite`, { email: inviteEmail.trim(), role: inviteRole });
      toast.success('Invite sent!');
      setInviteEmail('');
      await loadTeamDetail(activeTeam.id);
      loadPendingInvites();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to invite');
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!activeTeam) return;
    try {
      await api.delete(`/teams/${activeTeam.id}/members/${memberId}`);
      toast.success('Member removed');
      await loadTeamDetail(activeTeam.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const isAdmin = activeTeam?.members?.some(m => m.user_id === user?.id && m.role === 'admin');

  const loadPendingInvites = useCallback(async () => {
    if (!activeTeam?.id) return;
    try {
      const r = await api.get(`/teams/${activeTeam.id}/invites`);
      setPendingTeamInvites(r.data || []);
    } catch { setPendingTeamInvites([]); }
  }, [activeTeam?.id]);

  useEffect(() => {
    if (tab === 'members' && isAdmin) loadPendingInvites();
    else setPendingTeamInvites([]);
  }, [tab, isAdmin, loadPendingInvites]);

  const handleResendTeamInvite = async (inviteId) => {
    try {
      await api.post(`/teams/${activeTeam.id}/invite/${inviteId}/resend`);
      toast.success('Invite email resent');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to resend'); }
  };

  const handleCancelTeamInvite = async (inviteId) => {
    try {
      await api.delete(`/teams/${activeTeam.id}/invite/${inviteId}`);
      setPendingTeamInvites(prev => prev.filter(i => i.id !== inviteId));
      toast.success('Invite cancelled');
    } catch (err) { toast.error('Failed to cancel invite'); }
  };

  useEffect(() => {
    setRateForm(activeTeam?.cost_rates ? mergeRates(activeTeam.cost_rates) : DEFAULT_RATES);
    setBrandName(activeTeam?.brand_name || '');
    setBrandLogo(activeTeam?.brand_logo || '');
    setBrandColor(activeTeam?.brand_color || '#FF4500');
  }, [activeTeam?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 400 * 1024) { toast.error('Logo must be under 400KB'); return; }
    const reader = new FileReader();
    reader.onload = () => setBrandLogo(reader.result);
    reader.readAsDataURL(file);
    if (e.target) e.target.value = '';
  };
  const handleSaveBranding = async () => {
    if (!activeTeam) return;
    setSavingBrand(true);
    try {
      await api.put(`/teams/${activeTeam.id}`, { brand_name: brandName, brand_logo: brandLogo, brand_color: brandColor });
      toast.success('Branding saved');
      await loadTeamDetail(activeTeam.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save branding');
    } finally { setSavingBrand(false); }
  };

  const setVideoRate = (provider, value, resolution) => {
    setRateForm(prev => {
      const next = { video: { ...prev.video }, image: { ...prev.image } };
      const num = value === '' ? 0 : parseFloat(value);
      if (resolution) {
        next.video[provider] = { perSecond: { ...next.video[provider].perSecond, [resolution]: isNaN(num) ? 0 : num } };
      } else {
        next.video[provider] = { perSecond: isNaN(num) ? 0 : num };
      }
      return next;
    });
  };
  const setImageRate = (provider, value) => {
    setRateForm(prev => {
      const num = value === '' ? 0 : parseFloat(value);
      return { video: { ...prev.video }, image: { ...prev.image, [provider]: isNaN(num) ? 0 : num } };
    });
  };
  const handleSaveRates = async () => {
    if (!activeTeam) return;
    setSavingRates(true);
    try {
      await api.put(`/teams/${activeTeam.id}/cost-rates`, { cost_rates: rateForm });
      toast.success('Cost rates saved');
      await loadTeamDetail(activeTeam.id);
      refreshRates();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save rates');
    } finally { setSavingRates(false); }
  };
  const handleResetRates = () => { setRateForm(DEFAULT_RATES); };

  return (
    <div className="min-h-screen bg-[#060608]" data-testid="team-settings-page">
      <main className="max-w-5xl mx-auto px-4 md:px-6 py-6 md:py-10">
        <div className="flex items-center gap-3 mb-8">
          <Settings className="w-6 h-6 text-[#FF4500]" />
          <h1 className="text-3xl sm:text-4xl font-black tracking-tighter" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Team Settings
          </h1>
        </div>

        <div className="flex gap-8">
          {/* Sidebar - Teams */}
          <div className="w-56 shrink-0">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs tracking-[0.2em] uppercase font-semibold text-[#55555C]">Teams</span>
              <button
                data-testid="new-team-btn"
                onClick={() => setShowNewTeam(!showNewTeam)}
                className="p-1 rounded hover:bg-white/5 text-[#55555C] hover:text-white transition-colors"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>

            {showNewTeam && (
              <div className="mb-3 flex gap-1.5">
                <input
                  data-testid="new-team-name-input"
                  type="text"
                  value={newTeamName}
                  onChange={(e) => setNewTeamName(e.target.value)}
                  className="ff-input text-xs flex-1"
                  placeholder="Team name"
                  onKeyDown={(e) => e.key === 'Enter' && handleCreateTeam()}
                />
                <button onClick={handleCreateTeam} className="p-1.5 rounded bg-[#FF4500] hover:bg-[#FF5722] text-white">
                  <Check className="w-3.5 h-3.5" />
                </button>
              </div>
            )}

            <div className="space-y-1">
              {teams.map(team => (
                <button
                  key={team.id}
                  data-testid={`team-sidebar-${team.id}`}
                  onClick={() => handleSwitchTeam(team.id)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all flex items-center justify-between group ${
                    team.is_active
                      ? 'bg-[#FF4500]/10 text-white border border-[#FF4500]/20'
                      : 'hover:bg-white/5 text-[#8A8A93]'
                  }`}
                >
                  <div className="truncate">
                    <div className="font-medium truncate">{team.name}</div>
                    <div className="text-[10px] text-[#55555C]">{team.member_count} member{team.member_count !== 1 ? 's' : ''}</div>
                  </div>
                  {team.is_active && <ChevronRight className="w-3.5 h-3.5 text-[#FF4500] shrink-0" />}
                </button>
              ))}
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {!activeTeam ? (
              <div className="text-center py-20 text-[#55555C]">Select a team from the sidebar</div>
            ) : (
              <>
                {/* Tabs */}
                <div className="flex gap-1 mb-6 bg-[#0E0E12] rounded-lg p-1 w-fit">
                  {[
                    { id: 'keys', label: 'API Keys', icon: Key },
                    { id: 'members', label: 'Members', icon: Users },
                    { id: 'usage', label: 'Usage', icon: BarChart3 },
                    { id: 'notifications', label: 'Notifications', icon: Bell },
                    ...(isAdmin ? [{ id: 'rates', label: 'Cost Rates', icon: DollarSign }, { id: 'branding', label: 'Branding', icon: Palette }] : []),
                  ].map(t => (
                    <button
                      key={t.id}
                      data-testid={`tab-${t.id}`}
                      onClick={() => { setTab(t.id); if (t.id === 'usage' && activeTeam) loadUsage(activeTeam.id); }}
                      className={`px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2 transition-all ${
                        tab === t.id
                          ? 'bg-[#1A1A20] text-white shadow-sm'
                          : 'text-[#55555C] hover:text-[#8A8A93]'
                      }`}
                    >
                      <t.icon className="w-3.5 h-3.5" /> {t.label}
                    </button>
                  ))}
                </div>

                {/* API Keys Tab */}
                {tab === 'keys' && (
                  <div data-testid="api-keys-section">
                    <div className="space-y-6">
                      {PROVIDER_GROUPS.map(group => (
                        <div key={group.category}>
                          <h3 className="text-xs tracking-[0.2em] uppercase font-semibold text-[#55555C] mb-3">{group.category}</h3>
                          <div className="space-y-2">
                            {group.providers.map(p => {
                              const savedMasked = activeTeam.api_keys?.[p.key];
                              const hasKey = !!savedMasked;
                              return (
                                <div key={p.key} className="bg-[#0E0E12] border border-white/[0.06] rounded-lg p-3">
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                      <div className={`w-2 h-2 rounded-full ${hasKey ? 'bg-emerald-400' : 'bg-[#55555C]'}`} />
                                      <span className="text-sm font-medium text-white">{p.label}</span>
                                      <span className="text-xs text-[#55555C]">{p.desc}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      {hasKey && (
                                        <span className="text-xs text-[#55555C] font-mono">{savedMasked}</span>
                                      )}
                                      {hasKey && (
                                        <button data-testid={`test-key-${p.key}`} onClick={() => testKey(p.key)} disabled={keyTests[p.key]?.loading}
                                          className="text-[10px] px-2 py-0.5 rounded-full border border-white/10 text-[#8A8A93] hover:text-white hover:border-white/20 flex items-center gap-1 disabled:opacity-50" title="Verify this key with the provider">
                                          {keyTests[p.key]?.loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <PlugZap className="w-3 h-3" />} Test
                                        </button>
                                      )}
                                      <a href={p.url} target="_blank" rel="noopener noreferrer" className="text-[#55555C] hover:text-[#FF4500] transition-colors">
                                        <ExternalLink className="w-3.5 h-3.5" />
                                      </a>
                                      {hasKey && isAdmin && (
                                        <button
                                          onClick={() => handleDeleteKey(p.key)}
                                          className="text-[#55555C] hover:text-red-400 transition-colors"
                                          title="Remove key"
                                        >
                                          <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                  {keyTests[p.key] && !keyTests[p.key].loading && (
                                    <p data-testid={`key-test-result-${p.key}`} className={`text-[11px] mb-2 flex items-start gap-1.5 ${keyTests[p.key].ok ? 'text-emerald-400' : 'text-amber-400'}`}>
                                      {keyTests[p.key].ok ? <Check className="w-3.5 h-3.5 mt-px shrink-0" /> : <AlertTriangle className="w-3.5 h-3.5 mt-px shrink-0" />}
                                      <span>{keyTests[p.key].message}</span>
                                    </p>
                                  )}
                                  {isAdmin && (
                                    <div className="relative">
                                      <input
                                        data-testid={`settings-key-${p.key}`}
                                        type={showKeys[p.key] ? 'text' : 'password'}
                                        value={apiKeys[p.key] || ''}
                                        onChange={(e) => setApiKeys(prev => ({ ...prev, [p.key]: e.target.value }))}
                                        className="ff-input w-full pr-10 text-sm"
                                        placeholder={hasKey ? 'Enter new key to replace' : `Paste your ${p.label} key`}
                                      />
                                      <button
                                        type="button"
                                        onClick={() => setShowKeys(prev => ({ ...prev, [p.key]: !prev[p.key] }))}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#55555C] hover:text-white transition-colors"
                                      >
                                        {showKeys[p.key] ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                                      </button>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>

                    {isAdmin && (
                      <button
                        data-testid="save-team-keys-btn"
                        onClick={handleSaveKeys}
                        disabled={savingKeys || Object.values(apiKeys).every(v => !v?.trim())}
                        className="ff-btn-primary mt-6 flex items-center gap-2 disabled:opacity-40"
                      >
                        {savingKeys ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Check className="w-4 h-4" />}
                        Save Changes
                      </button>
                    )}
                  </div>
                )}

                {/* Members Tab */}
                {tab === 'members' && (
                  <div data-testid="members-section">
                    {isAdmin && (
                      <div className="bg-[#0E0E12] border border-white/[0.06] rounded-lg p-4 mb-6">
                        <h3 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                          <UserPlus className="w-4 h-4 text-[#FF4500]" /> Invite Member
                        </h3>
                        <div className="flex gap-2">
                          <input
                            data-testid="invite-member-email"
                            type="email"
                            value={inviteEmail}
                            onChange={(e) => setInviteEmail(e.target.value)}
                            className="ff-input flex-1"
                            placeholder="Email address"
                          />
                          <select
                            data-testid="invite-member-role"
                            value={inviteRole}
                            onChange={(e) => setInviteRole(e.target.value)}
                            className="ff-input w-32"
                          >
                            <option value="member">Member</option>
                            <option value="admin">Admin</option>
                          </select>
                          <button
                            data-testid="send-invite-btn"
                            onClick={handleInvite}
                            className="ff-btn-primary px-4"
                          >
                            Invite
                          </button>
                        </div>
                      </div>
                    )}

                    <div className="space-y-2">
                      {activeTeam.members?.map(member => (
                        <div
                          key={member.user_id}
                          data-testid={`member-${member.user_id}`}
                          className="bg-[#0E0E12] border border-white/[0.06] rounded-lg p-3 flex items-center justify-between"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-[#FF4500]/10 flex items-center justify-center">
                              <span className="text-sm font-bold text-[#FF4500]">
                                {(member.name || member.email || '?')[0].toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-white">{member.name || 'Unknown'}</p>
                              <p className="text-xs text-[#55555C]">{member.email}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                              member.role === 'admin'
                                ? 'bg-[#FF4500]/10 text-[#FF4500]'
                                : 'bg-white/5 text-[#8A8A93]'
                            }`}>
                              {member.role === 'admin' && <Crown className="w-3 h-3 inline mr-1" />}
                              {member.role}
                            </span>
                            {isAdmin && member.user_id !== activeTeam.owner_id && member.user_id !== user?.id && (
                              <button
                                onClick={() => handleRemoveMember(member.user_id)}
                                className="text-[#55555C] hover:text-red-400 transition-colors"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    {isAdmin && pendingTeamInvites.length > 0 && (
                      <div className="mt-6" data-testid="pending-invites-section">
                        <h3 className="text-xs tracking-[0.2em] uppercase font-semibold text-[#55555C] mb-3">Pending invites</h3>
                        <div className="space-y-2">
                          {pendingTeamInvites.map(inv => (
                            <div key={inv.id} data-testid={`pending-invite-${inv.id}`}
                              className="bg-[#0E0E12] border border-dashed border-white/10 rounded-lg p-3 flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-full bg-white/5 flex items-center justify-center">
                                  <Mail className="w-4 h-4 text-[#8A8A93]" />
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-white">{inv.invitee_email}</p>
                                  <p className="text-xs text-[#55555C]">
                                    Invited by {inv.inviter_name || 'admin'}{inv.role === 'admin' ? ' · admin' : ''} · awaiting response
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                <button
                                  data-testid={`resend-team-invite-${inv.id}`}
                                  onClick={() => handleResendTeamInvite(inv.id)}
                                  className="text-xs text-[#FF4500] hover:text-[#FF6a33] transition-colors font-medium"
                                >
                                  Resend
                                </button>
                                <button
                                  data-testid={`cancel-team-invite-${inv.id}`}
                                  onClick={() => handleCancelTeamInvite(inv.id)}
                                  className="text-xs text-[#8A8A93] hover:text-red-400 transition-colors"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Usage Tab */}
                {tab === 'usage' && (
                  <div data-testid="usage-section">
                    {loadingUsage ? (
                      <div className="text-center py-12 text-[#55555C]">Loading usage data...</div>
                    ) : !usageData ? (
                      <div className="text-center py-12 text-[#55555C]">Click the Usage tab to load data</div>
                    ) : (
                      <div className="space-y-6">
                        {/* Summary Cards */}
                        <div className="grid grid-cols-3 gap-3">
                          <div className="bg-[#0E0E12] border border-white/[0.06] rounded-lg p-4">
                            <p className="text-xs text-[#55555C] mb-1">Total Generations (30d)</p>
                            <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{usageData.total}</p>
                          </div>
                          <div className="bg-[#0E0E12] border border-white/[0.06] rounded-lg p-4">
                            <p className="text-xs text-[#55555C] mb-1">Providers Used</p>
                            <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{Object.keys(usageData.by_provider || {}).length}</p>
                          </div>
                          <div className="bg-[#0E0E12] border border-white/[0.06] rounded-lg p-4">
                            <p className="text-xs text-[#55555C] mb-1">Active Days</p>
                            <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{Object.keys(usageData.by_day || {}).length}</p>
                          </div>
                        </div>

                        {/* By Action */}
                        <div className="bg-[#0E0E12] border border-white/[0.06] rounded-lg p-4">
                          <h3 className="text-xs tracking-[0.2em] uppercase font-semibold text-[#55555C] mb-3">By Type</h3>
                          {Object.keys(usageData.by_action || {}).length === 0 ? (
                            <p className="text-xs text-[#55555C]">No usage recorded yet</p>
                          ) : (
                            <div className="space-y-2">
                              {Object.entries(usageData.by_action).map(([action, count]) => (
                                <div key={action} className="flex items-center justify-between">
                                  <span className="text-sm text-[#8A8A93] capitalize">{action.replace(/_/g, ' ')}</span>
                                  <div className="flex items-center gap-2">
                                    <div className="w-24 h-1.5 bg-[#1A1A20] rounded-full overflow-hidden">
                                      <div className="h-full bg-[#FF4500] rounded-full" style={{ width: `${Math.min(100, (count / usageData.total) * 100)}%` }} />
                                    </div>
                                    <span className="text-xs font-medium text-white w-8 text-right">{count}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* By Provider */}
                        <div className="bg-[#0E0E12] border border-white/[0.06] rounded-lg p-4">
                          <h3 className="text-xs tracking-[0.2em] uppercase font-semibold text-[#55555C] mb-3">By Provider</h3>
                          <div className="flex flex-wrap gap-2">
                            {Object.entries(usageData.by_provider || {}).length === 0 ? (
                              <p className="text-xs text-[#55555C]">No providers used yet</p>
                            ) : Object.entries(usageData.by_provider).map(([provider, count]) => (
                              <span key={provider} className="px-3 py-1.5 rounded-lg bg-white/5 text-xs font-medium text-[#8A8A93] border border-white/[0.06]">
                                {provider} <span className="text-white ml-1">{count}</span>
                              </span>
                            ))}
                          </div>
                        </div>

                        {/* Recent Activity */}
                        <div className="bg-[#0E0E12] border border-white/[0.06] rounded-lg p-4">
                          <h3 className="text-xs tracking-[0.2em] uppercase font-semibold text-[#55555C] mb-3">Recent Activity</h3>
                          <div className="space-y-1.5 max-h-64 overflow-y-auto">
                            {(usageData.recent || []).map((log, i) => (
                              <div key={log.id || i} className="flex items-center justify-between py-1.5 border-b border-white/[0.03] last:border-0">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-white capitalize">{(log.action || '').replace(/_/g, ' ')}</span>
                                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-[#8A8A93]">{log.provider}</span>
                                  {log.detail && <span className="text-[10px] text-[#55555C]">{log.detail}</span>}
                                </div>
                                <span className="text-[10px] text-[#55555C]">{log.created_at?.slice(0, 16).replace('T', ' ')}</span>
                              </div>
                            ))}
                            {(usageData.recent || []).length === 0 && (
                              <p className="text-xs text-[#55555C]">No activity yet</p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Notifications Tab */}
                {tab === 'notifications' && (
                  <div className="max-w-2xl space-y-3" data-testid="notifications-tab">
                    <p className="text-[10px] tracking-[0.15em] uppercase font-semibold text-[#8A8A93] mb-1">Email me about</p>
                    {[
                      { key: 'mentions', icon: AtSign, title: 'Missed @mentions', desc: "If you're @mentioned and haven't opened that chat within 30 minutes, we'll email you the message with a link. One email per mention." },
                      { key: 'invites', icon: UserPlus, title: 'Invitations', desc: 'Get an email when someone invites you to a project or workspace, so you never miss one.' },
                      { key: 'welcome', icon: Check, title: 'Welcome messages', desc: 'Receive a friendly welcome email when you accept an invite and join a project or workspace.' },
                    ].map(row => (
                      <div key={row.key} className="ff-card p-5">
                        <div className="flex items-start gap-4">
                          <div className="w-10 h-10 rounded-lg bg-[#FF4500]/15 text-[#FF4500] flex items-center justify-center shrink-0">
                            <row.icon className="w-5 h-5" />
                          </div>
                          <div className="flex-1">
                            <h3 className="text-white font-semibold text-base" style={{ fontFamily: 'Outfit, sans-serif' }}>{row.title}</h3>
                            <p className="text-[#8A8A93] text-sm mt-1 leading-relaxed">{row.desc}</p>
                          </div>
                          <button
                            data-testid={`toggle-email-${row.key}`}
                            role="switch"
                            aria-checked={prefs[row.key]}
                            disabled={savingPref}
                            onClick={() => togglePref(row.key)}
                            className={`relative w-12 h-7 rounded-full shrink-0 transition-colors ${prefs[row.key] ? 'bg-[#FF4500]' : 'bg-white/10'} disabled:opacity-60`}
                          >
                            <span className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-white transition-transform ${prefs[row.key] ? 'translate-x-5' : ''}`} />
                          </button>
                        </div>
                      </div>
                    ))}
                    <p className="text-xs text-[#55555C] leading-relaxed pt-1">These are personal settings and apply to your account across all workspaces.</p>
                  </div>
                )}


                {/* Cost Rates Tab */}
                {tab === 'rates' && isAdmin && (
                  <div data-testid="cost-rates-section" className="max-w-2xl">
                    <div className="flex items-start gap-3 mb-6">
                      <div className="w-10 h-10 rounded-lg bg-[#10B981]/10 flex items-center justify-center shrink-0"><DollarSign className="w-5 h-5 text-[#10B981]" /></div>
                      <div>
                        <h3 className="text-white font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Cost Estimate Rates</h3>
                        <p className="text-xs text-[#8A8A93] mt-1">Approximate provider prices used for the on-screen cost estimates and project roll-up. Editing these does not change what providers actually bill you.</p>
                      </div>
                    </div>

                    <div className="space-y-5">
                      <div>
                        <p className="text-[10px] tracking-[0.15em] uppercase font-semibold text-[#E879F9] mb-2">Video — USD per second</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div className="ff-card p-3">
                            <label className="text-xs text-white block mb-1">Kling 3.0</label>
                            <input data-testid="rate-video-kling" type="number" step="0.01" min="0" value={rateForm.video.kling.perSecond}
                              onChange={e => setVideoRate('kling', e.target.value)} className="ff-input w-full px-2 py-1.5 text-sm rounded-lg" />
                          </div>
                          <div className="ff-card p-3">
                            <label className="text-xs text-white block mb-1">Veo 3.1</label>
                            <input data-testid="rate-video-veo" type="number" step="0.01" min="0" value={rateForm.video.veo.perSecond}
                              onChange={e => setVideoRate('veo', e.target.value)} className="ff-input w-full px-2 py-1.5 text-sm rounded-lg" />
                          </div>
                        </div>
                        <div className="ff-card p-3 mt-3">
                          <label className="text-xs text-white block mb-2">Seedance 2.5 (by resolution)</label>
                          <div className="grid grid-cols-3 gap-2">
                            {['480p', '720p', '1080p'].map(r => (
                              <div key={r}>
                                <span className="text-[10px] text-[#8A8A93] block mb-1">{r}</span>
                                <input data-testid={`rate-video-seedance-${r}`} type="number" step="0.01" min="0" value={rateForm.video.seedance.perSecond[r]}
                                  onChange={e => setVideoRate('seedance', e.target.value, r)} className="ff-input w-full px-2 py-1.5 text-sm rounded-lg" />
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div>
                        <p className="text-[10px] tracking-[0.15em] uppercase font-semibold text-[#A78BFA] mb-2">Images — USD per image</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div className="ff-card p-3">
                            <label className="text-xs text-white block mb-1">Gemini (Nano Banana)</label>
                            <input data-testid="rate-image-gemini" type="number" step="0.01" min="0" value={rateForm.image.gemini}
                              onChange={e => setImageRate('gemini', e.target.value)} className="ff-input w-full px-2 py-1.5 text-sm rounded-lg" />
                          </div>
                          <div className="ff-card p-3">
                            <label className="text-xs text-white block mb-1">OpenAI (GPT Image)</label>
                            <input data-testid="rate-image-openai" type="number" step="0.01" min="0" value={rateForm.image.openai}
                              onChange={e => setImageRate('openai', e.target.value)} className="ff-input w-full px-2 py-1.5 text-sm rounded-lg" />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 mt-6">
                      <button data-testid="save-rates-btn" onClick={handleSaveRates} disabled={savingRates}
                        className="ff-btn-primary flex items-center gap-2 text-sm disabled:opacity-40">
                        {savingRates ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save Rates
                      </button>
                      <button data-testid="reset-rates-btn" onClick={handleResetRates}
                        className="ff-btn-secondary flex items-center gap-2 text-sm">Reset to defaults</button>
                    </div>
                  </div>
                )}

                {/* Branding Tab */}
                {tab === 'branding' && isAdmin && (
                  <div data-testid="branding-section" className="max-w-xl">
                    <div className="flex items-start gap-3 mb-6">
                      <div className="w-10 h-10 rounded-lg bg-[#00F0FF]/10 flex items-center justify-center shrink-0"><Palette className="w-5 h-5 text-[#00F0FF]" /></div>
                      <div>
                        <h3 className="text-white font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>White-label Branding</h3>
                        <p className="text-xs text-[#8A8A93] mt-1">Your logo and name appear on public share pages clients see. Cost info always stays private.</p>
                      </div>
                    </div>
                    <div className="space-y-5">
                      <div className="ff-card p-4">
                        <label className="text-xs text-white block mb-2">Brand name</label>
                        <input data-testid="brand-name-input" value={brandName} onChange={e => setBrandName(e.target.value)}
                          placeholder="e.g. Your Studio Name" className="ff-input w-full px-3 py-2 text-sm rounded-lg" />
                      </div>
                      <div className="ff-card p-4">
                        <label className="text-xs text-white block mb-2">Logo</label>
                        <div className="flex items-center gap-3">
                          <div className="w-16 h-16 rounded-lg bg-[#0a0a0e] border border-white/[0.06] flex items-center justify-center overflow-hidden shrink-0">
                            {brandLogo ? <img src={brandLogo} alt="logo" data-testid="brand-logo-preview" className="w-full h-full object-contain" /> : <ImageIcon className="w-6 h-6 text-[#55555C]" />}
                          </div>
                          <input ref={logoInputRef} type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                          <button data-testid="upload-logo-btn" onClick={() => logoInputRef.current?.click()}
                            className="ff-btn-secondary flex items-center gap-2 text-sm"><Upload className="w-4 h-4" /> Upload logo</button>
                          {brandLogo && (
                            <button data-testid="remove-logo-btn" onClick={() => setBrandLogo('')}
                              className="text-xs text-red-400 hover:text-red-300">Remove</button>
                          )}
                          <span className="text-[10px] text-[#55555C]">PNG/JPG, under 400KB</span>
                        </div>
                      </div>
                      <div className="ff-card p-4">
                        <label className="text-xs text-white block mb-2">Accent color (used in emails)</label>
                        <div className="flex items-center gap-3">
                          <input type="color" data-testid="brand-color-input" value={brandColor} onChange={e => setBrandColor(e.target.value)}
                            className="w-12 h-10 rounded-lg bg-transparent border border-white/10 cursor-pointer p-0.5" />
                          <input type="text" value={brandColor} onChange={e => setBrandColor(e.target.value)}
                            className="ff-input w-28 px-3 py-2 text-sm rounded-lg font-mono" placeholder="#FF4500" />
                          <span className="text-[10px] text-[#55555C]">Buttons & highlights in invite / welcome / mention emails</span>
                        </div>
                      </div>
                    </div>
                    <div className="mt-6">
                      <button data-testid="save-branding-btn" onClick={handleSaveBranding} disabled={savingBrand}
                        className="ff-btn-primary flex items-center gap-2 text-sm disabled:opacity-40">
                        {savingBrand ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save Branding
                      </button>
                    </div>
                  </div>
                )}

              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
